import { createSelector } from 'reselect';
import { selectors as concurrencySelectors } from '@saucelabs/sl-module-concurrency';
import { selectors as userSelectors, OrgPlansNotFetchedError } from '@saucelabs/sl-module-user';
import { selectors as usageSelectors } from '@saucelabs/sl-module-usage';
import { selectors as tunnelsSelectors } from '@saucelabs/sl-module-tunnels';

import utils from './utils';
import { getDaysUntilRefresh } from '../../utils';

const deviceTypeSelector = (state) => state.apps.manual.testConfiguration.get('deviceType');
const urlSelector = (state) => state.apps.manual.testConfiguration.get('url');
const isSessionLoadingSelector = (state) => state.apps.manual.testConfiguration.get('isSessionLoading');
const tunnelSelector = (state) => state.apps.manual.testConfiguration.get('tunnel');
const saveConfigurationSelector = (state) => state.apps.manual.testConfiguration.get('saveConfiguration');
const mobileAppConfigurationSelector = (state) => state.apps.manual.testConfiguration.get('mobileAppConfiguration');
const browserConfigurationSelector = (state) => state.apps.manual.testConfiguration.get('browserConfiguration');
const browserOptionsSelector = (state) => state.apps.manual.testConfiguration.get('browserOptions');
const browserOptionsLoadedSelector = (state) => state.apps.manual.testConfiguration.get('browserOptionsLoaded');
const realDeviceConfigurationSelector = (state) => state.apps.manual.testConfiguration.get('realDeviceConfiguration');
const testTypeSelector = (state) => state.apps.manual.testConfiguration.get('testType');
const virtualDeviceConfigurationSelector = (state) => state.apps.manual.testConfiguration.get('virtualDeviceConfiguration');
const virtualDeviceOptionsSelector = (state) => state.apps.manual.testConfiguration.get('virtualDeviceOptions');
const virtualDeviceOptionsLoadedSelector = (state) => state.apps.manual.testConfiguration.get('virtualDeviceOptionsLoaded');
const configurationLoadedSelector = (state) => state.apps.manual.testConfiguration.get('configurationLoaded');

const desktopBrowserConfigSelector = createSelector([
  browserConfigurationSelector,
  tunnelSelector,
  urlSelector,
  browserOptionsSelector,
], (
  browserConfiguration,
  tunnel,
  url,
  browserOptions,
) => ({
  browserConfiguration,
  tunnel,
  url,
  browserOptions,
}));

const tunnelConfigSelector = createSelector(
  tunnelsSelectors.getAllTunnels,
  (allTunnels) => ({ tunnelOptions: utils.prepTunnelOptions(allTunnels) }),
);

const handleTabsFormSubmitSelector = createSelector([
  browserConfigurationSelector,
  deviceTypeSelector,
  mobileAppConfigurationSelector,
  realDeviceConfigurationSelector,
  testTypeSelector,
  tunnelSelector,
  urlSelector,
  virtualDeviceConfigurationSelector,
], (
  browserConfiguration,
  deviceType,
  mobileAppConfiguration,
  realDeviceConfiguration,
  testType,
  tunnel,
  url,
  virtualDeviceConfiguration,
) => ({
  browserConfiguration,
  deviceType,
  mobileAppConfiguration,
  realDeviceConfiguration,
  testType,
  tunnel,
  url,
  virtualDeviceConfiguration,
}));

const isVirtualSubmitDisabledSelector = createSelector([
  browserConfigurationSelector,
  deviceTypeSelector,
  concurrencySelectors.hasVdcInUserPlanSelector,
  userSelectors.isExpiredUserSelector,
  concurrencySelectors.isUserConcurrencyVdcMaxedOutSelector,
  userSelectors.isVerifiedUserSelector,
  isSessionLoadingSelector,
  mobileAppConfigurationSelector,
  realDeviceConfigurationSelector,
  testTypeSelector,
  virtualDeviceConfigurationSelector,
], (
  browserConfiguration,
  deviceType,
  hasVdcInUserPlan,
  isExpiredUser,
  isUserConcurrencyVdcMaxedOut,
  isVerifiedUser,
  isSessionLoading,
  mobileAppConfiguration,
  realDeviceConfiguration,
  testType,
  virtualDeviceConfiguration,
) => !isVerifiedUser || isSessionLoading || !!utils.getValidationErrors({
  deviceType,
  browserConfiguration,
  mobileAppConfiguration,
  realDeviceConfiguration,
  testType,
  virtualDeviceConfiguration,
}) || isExpiredUser || !hasVdcInUserPlan || isUserConcurrencyVdcMaxedOut);

const testConfigurationSelector = createSelector([
  browserConfigurationSelector,
  deviceTypeSelector,
  isSessionLoadingSelector,
  mobileAppConfigurationSelector,
  realDeviceConfigurationSelector,
  saveConfigurationSelector,
  testTypeSelector,
  tunnelSelector,
  urlSelector,
  virtualDeviceConfigurationSelector,
], (
  browserConfiguration,
  deviceType,
  isSessionLoading,
  mobileAppConfiguration,
  realDeviceConfiguration,
  saveConfiguration,
  testType,
  tunnel,
  url,
  virtualDeviceConfiguration,
) => ({
  browserConfiguration,
  deviceType,
  isSessionLoading,
  mobileAppConfiguration,
  realDeviceConfiguration,
  saveConfiguration,
  testType,
  tunnel,
  url,
  virtualDeviceConfiguration,
}));

const useCheckIfCurrentTunnelAvailableSelector = createSelector([
  tunnelsSelectors.getAllTunnels,
  tunnelSelector,
], (
  allTunnels,
  tunnel,
) => ({
  allTunnels,
  tunnel,
}));

// WARNING: This selector (and selectors including this selectors) may only be used in containers
// that fetch account's plans information (via getOrgPlans or loggedInUserWithOrgPlans). If neither
// is called before the selector is applied, it'll always return false.
const vdcWarningsSelector = createSelector([
  concurrencySelectors.hasVdcInUserPlanSelector,
  userSelectors.isExpiredUserSelector,
  userSelectors.isOrgAdminSelector,
  concurrencySelectors.isUserConcurrencyVdcMaxedOutSelector,
  concurrencySelectors.userConcurrencyAllowedVdcSelector,
  usageSelectors.availableUsageUnitsSelector,
  (store) => store.user.getOrgUserType(),
  (store) => {
    const creditRefreshDate = store.usage?.nextFreemiumReplenishment;
    return getDaysUntilRefresh(creditRefreshDate);
  },
  (store) => store.user,
], (
  hasVdcInPlan,
  isExpiredUser,
  isOrgAdmin,
  isUserConcurrencyVdcMaxedOut,
  userConcurrencyAllowedVdc,
  usageUnits,
  orgUserType,
  daysUntilRefresh,
  user,
) => {
  let isFreemiumCapable = false;
  try {
    isFreemiumCapable = user.isFreemiumCapable();
  } catch (error) {
    if (!(error instanceof OrgPlansNotFetchedError)) {
      throw error;
    }
  }

  return {
    hasVdcInPlan,
    isExpiredUser,
    isOrgAdmin,
    isUserConcurrencyVdcMaxedOut,
    userConcurrencyAllowedVdc,
    usageUnits,
    orgUserType,
    daysUntilRefresh,
    isFreemiumCapable,
  };
});

const virtualDeviceConfiguratorSelector = createSelector([
  mobileAppConfigurationSelector,
  testTypeSelector,
  tunnelSelector,
  urlSelector,
  virtualDeviceOptionsSelector,
], (
  mobileAppConfiguration,
  testType,
  tunnel,
  url,
  virtualDeviceOptions,
) => ({
  virtualDeviceOptions,
  mobileAppConfiguration,
  testType,
  tunnel,
  url,
}));
export default {
  browserConfigurationSelector,
  browserOptionsSelector,
  browserOptionsLoadedSelector,
  configurationLoadedSelector,
  desktopBrowserConfigSelector,
  deviceTypeSelector,
  handleTabsFormSubmitSelector,
  isSessionLoadingSelector,
  isVirtualSubmitDisabledSelector,
  mobileAppConfigurationSelector,
  realDeviceConfigurationSelector,
  testConfigurationSelector,
  testTypeSelector,
  tunnelConfigSelector,
  tunnelSelector,
  useCheckIfCurrentTunnelAvailableSelector,
  urlSelector,
  vdcWarningsSelector,
  virtualDeviceConfigurationSelector,
  virtualDeviceConfiguratorSelector,
  virtualDeviceOptionsSelector,
  virtualDeviceOptionsLoadedSelector,
};
